import React, { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import styled from 'styled-components';
import { useApi } from '~/contexts';
import { colors } from '~/styles';
import { Section, SectionTitle } from './shared';

const Wrapper = styled.div`
  height: ${({ $rows }) => 4 + $rows * 2}rem;
`;

function ToolChart({ title, tools }) {
  const chartData = {
    labels: tools.map((d) => d.tool),
    datasets: [
      {
        label: 'Successful',
        data: tools.map((d) => d.calls),
        backgroundColor: colors.success,
        barThickness: 16,
      },
      {
        label: 'Errors',
        data: tools.map((d) => d.errors),
        backgroundColor: colors.danger,
        barThickness: 16,
      },
    ],
  };

  return (
    <Section>
      <SectionTitle>{title}</SectionTitle>
      <Wrapper $rows={tools.length}>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: 'y',
            interaction: { mode: 'index' },
            scales: {
              x: { stacked: true, ticks: { precision: 0 } },
              y: { stacked: true },
            },
            plugins: {
              tooltip: {
                callbacks: {
                  footer: (items) => {
                    const total = items.reduce((sum, item) => sum + item.raw, 0);
                    return `Total: ${total}`;
                  },
                },
              },
            },
          }}
        />
      </Wrapper>
    </Section>
  );
}

export default function ToolCalls({ start, end }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data } = await api.admin.aiDashboard.getToolCalls({ start, end });
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end]);

  const { domain, system } = useMemo(() => {
    if (!data) return { domain: null, system: null };

    const domainTools = data
      .filter((t) => t.namespace !== 'system')
      .sort((a, b) => b.calls + b.errors - (a.calls + a.errors));
    const systemTools = data
      .filter((t) => t.namespace === 'system')
      .sort((a, b) => b.calls + b.errors - (a.calls + a.errors));

    return { domain: domainTools, system: systemTools };
  }, [data]);

  if (isLoading || !data) return null;

  return (
    <>
      {domain && domain.length > 0 && <ToolChart title="Domain Tools" tools={domain} />}
      {system && system.length > 0 && <ToolChart title="System Tools" tools={system} />}
    </>
  );
}
